import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import {
  ArrowUpRight,
  Layers,
  Boxes,
  QrCode,
  Cpu,
  Building2,
  Map,
} from "lucide-react";

const STATS = [
  { key: "budova.stat.years", value: "35+" },
  { key: "budova.stat.complexes", value: "30+" },
  { key: "budova.stat.sqm", value: "1.000.000+" },
  { key: "budova.stat.families", value: "30.000+" },
];

const PILLARS_BY_LANG = {
  de: [
    { icon: Cpu, titleKey: "budova.system.title.l1", text: "BIM ↔ ERP-Integration: jedes Modellelement ist mit Material, Lieferant, Kostenstelle und Zeitfenster verknüpft. Eine einzige Wahrheit für Architekt:innen, Tragwerk, TGA und Finanzen." },
    { icon: Boxes, titleKey: "budova.system.title.l2", text: "Wand-, Decken-, Schacht- und Fassadenmodule unter Werksbedingungen gefertigt. TGA vorintegriert. Qualitätssicherung passiert dort, wo sie hingehört — am Produktionsband, nicht auf der Baustelle." },
    { icon: QrCode, titleKey: "budova.system.title.l3", text: "Jedes Bauteil trägt einen QR-Code mit Datenblatt, Montageabfolge, Prüfprotokollen und Wartungsanleitung — vom Smartphone aus auf der Baustelle abrufbar." },
  ],
  en: [
    { icon: Cpu, titleKey: "budova.system.title.l1", text: "BIM ↔ ERP integration: every model element is mapped to material, supplier, cost code and time slot. Single source of truth across architects, structural, MEP and finance." },
    { icon: Boxes, titleKey: "budova.system.title.l2", text: "Wall, slab, shaft and façade modules built in factory conditions. MEP pre-integrated. Quality assurance moved upstream — to the production line, not the building site." },
    { icon: QrCode, titleKey: "budova.system.title.l3", text: "Every component carries a QR code linking to spec sheet, assembly sequence, inspection records and maintenance manuals — accessible from any phone on site." },
  ],
  ru: [
    { icon: Cpu, titleKey: "budova.system.title.l1", text: "BIM ↔ ERP-интеграция: каждый элемент модели связан с материалом, поставщиком, статьёй затрат и слотом времени. Единый источник истины для всех специалистов." },
    { icon: Boxes, titleKey: "budova.system.title.l2", text: "Стены, плиты, шахты и фасадные модули — заводское изготовление. ИОС интегрированы. Контроль качества — на производстве, а не на стройке." },
    { icon: QrCode, titleKey: "budova.system.title.l3", text: "У каждого элемента QR-код: спецификация, монтаж, протоколы и обслуживание — со смартфона прямо на стройке." },
  ],
};

const PILLARS_TITLE_BY_LANG = {
  de: { eyebrow: "Referenz-Wohnkomplexe", title1: "Aus Odessa", title2: "in die Welt.", btn: "Alle Komplexe auf budova.ua", three_pillars_title: "Drei Säulen. Ein System." },
  en: { eyebrow: "Reference complexes", title1: "From Odesa", title2: "to the world.", btn: "All complexes on budova.ua", three_pillars_title: "Three pillars. One system." },
  ru: { eyebrow: "Эталонные комплексы", title1: "Из Одессы", title2: "в мир.", btn: "Все комплексы на budova.ua", three_pillars_title: "Три основы. Одна система." },
};

const COMPLEXES = [
  { name: "Sky City Plus", area: "Kyivskyi rajon, Varnenska 27-A/2", status: "In Bau" },
  { name: "Altair-3", area: "Kyivskyi rajon, Zhabotinskoho 54a", status: "In Bau", sqm: "43,6–87,4 m²", price: "ab 1.822.700 ₴" },
  { name: "Морська резиденція", area: "Prymorskyi rajon, Akademichna 30", status: "In Bau", sqm: "35,3–65,5 m²", price: "ab 2.122.000 ₴" },
  { name: "Oscar", area: "Prymorskyi rajon, Hagarina 9", status: "In Bau", sqm: "44,4–105,5 m²", price: "ab 2.533.100 ₴" },
  { name: "Sea View", area: "Prymorskyi rajon, Hagarinske Plato 4", status: "In Bau" },
  { name: "ЖК на Євреській 3", area: "Prymorskyi rajon, Yevreiska 3", status: "In Bau" },
];

export default function PartnerBudovaPage() {
  const { t, lang } = useLang();
  const PILLARS = PILLARS_BY_LANG[lang] || PILLARS_BY_LANG.ru || PILLARS_BY_LANG.de;
  const TITLES = PILLARS_TITLE_BY_LANG[lang] || PILLARS_TITLE_BY_LANG.ru || PILLARS_TITLE_BY_LANG.de;

  return (
    <div data-testid="budova-page" className="pt-32">
      <PageMeta pageKey="budova" />
      {/* Hero */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 pb-16">
        <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">
          {t("budova.eyebrow")}
        </p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-light uppercase tracking-tighter leading-[0.95]">
          {t("budova.title.l1")} <br />
          <span className="italic">{t("budova.title.l2")}</span>
        </h1>
        <p className="mt-10 max-w-3xl text-lg md:text-xl text-white/70 font-light leading-relaxed">
          {t("budova.lede")}
        </p>
      </section>

      {/* Stats */}
      <section className="border-y border-white/10 bg-[#0A2244]/40">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-2 md:grid-cols-4">
          {STATS.map((s, i) => (
            <div
              key={s.key}
              className={`py-10 md:py-14 ${i !== 0 ? "md:border-l border-white/10" : ""} ${
                i % 2 !== 0 ? "border-l border-white/10 md:border-l" : ""
              } ${i >= 2 ? "border-t border-white/10 md:border-t-0" : ""}`}
              data-testid={`budova-stat-${i}`}
            >
              <p className="font-display text-4xl md:text-5xl font-light tracking-tight">
                {s.value}
              </p>
              <p className="mt-3 text-[11px] tracking-[0.25em] uppercase text-white/50">
                {t(s.key)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-10 lg:gap-16">
        <div className="md:col-span-5">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">
            {t("budova.story.eyebrow")}
          </p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05]">
            {t("budova.story.title")}
          </h2>
        </div>
        <div className="md:col-span-7 md:pt-12 space-y-6">
          <p className="text-base md:text-lg text-white/70 leading-relaxed font-light">
            {t("budova.story.body")}
          </p>
          <a
            href="https://budova.ua/ua/"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="budova-external-link-story"
            className="rh-link inline-flex items-center gap-3 text-sm tracking-[0.18em] uppercase font-medium pt-4"
          >
            {t("budova.cta")} <ArrowUpRight size={16} strokeWidth={1.5} />
          </a>
        </div>
      </section>

      {/* Image strip */}
      <section className="grid md:grid-cols-3 border-y border-white/10">
        {[
          "/images/budova-strip-1.jpg",
          "/images/budova-strip-2.jpg",
          "/images/budova-strip-3.jpg",
        ].map((src, i) => (
          <div
            key={src}
            className={`aspect-[4/3] overflow-hidden ${i !== 0 ? "border-t md:border-t-0 md:border-l border-white/10" : ""}`}
          >
            <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
          </div>
        ))}
      </section>

      {/* System pillars */}
      <section className="py-24 md:py-32 bg-[#0A2244]/40 border-b border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">
            {t("budova.system.eyebrow")}
          </p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16 max-w-3xl">
            {TITLES.three_pillars_title.split(".").map((part, i, arr) =>
              part.trim() ? (
                <span key={`pillar-title-${part.trim()}`}>
                  {part.trim()}.{i < arr.length - 1 && <br />}
                </span>
              ) : null
            )}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-white/10">
            {PILLARS.map((p, i) => (
              <div
                key={p.titleKey}
                className="border-b border-r border-white/10 p-8 md:p-10"
                data-testid={`budova-pillar-${i}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-display text-3xl text-white/30">0{i + 1}</span>
                  <p.icon size={28} strokeWidth={1.25} />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mb-4 leading-tight">
                  {t(p.titleKey)}
                </h3>
                <p className="text-sm text-white/65 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reference complexes */}
      <section className="py-24 md:py-32 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
        <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{TITLES.eyebrow}</p>
        <div className="flex items-end justify-between gap-8 mb-12 flex-wrap">
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] max-w-3xl">
            {TITLES.title1} <br />{TITLES.title2}
          </h2>
          <a
            href="https://budova.ua/ua/sales/"
            target="_blank"
            rel="noreferrer noopener"
            className="rh-link text-sm tracking-[0.18em] uppercase font-medium inline-flex items-center gap-3"
            data-testid="budova-external-link-sales"
          >
            {TITLES.btn} <ArrowUpRight size={16} strokeWidth={1.5} />
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
          {COMPLEXES.map((c, i) => (
            <div
              key={c.name}
              className="border-b border-r border-white/10 p-8 md:p-10 group hover:bg-white/[0.03] transition-colors"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-display text-2xl text-white/30">0{i + 1}</span>
                <Building2 size={24} strokeWidth={1.25} />
              </div>
              <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight">{c.name}</h3>
              <p className="mt-3 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-white/55">
                <Map size={12} strokeWidth={1.5} />
                {c.area}
              </p>
              <p className="mt-2 text-[11px] tracking-[0.25em] uppercase text-white/50">{c.status}</p>
              {(c.sqm || c.price) && (
                <div className="mt-5 pt-5 border-t border-white/10 flex items-center gap-6">
                  {c.sqm && (
                    <span className="text-[11px] tracking-[0.15em] uppercase text-white/55">{c.sqm}</span>
                  )}
                  {c.price && (
                    <span className="font-display text-sm text-white/80">{c.price}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-white/10 bg-[#051630] relative overflow-hidden">
        <div className="absolute inset-0 rh-grid-bg opacity-40" />
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("home.cta.eyebrow")}</p>
            <h2 className="font-display text-4xl md:text-6xl font-light uppercase tracking-tight leading-[1.02]">
              Industrieller Standard. <br />
              <span className="italic">Wienerische</span> Eleganz.
            </h2>
          </div>
          <div className="md:col-span-4">
            <Link
              to="/kontakt"
              className="rh-btn-primary inline-flex items-center justify-center gap-3 bg-white text-[#051630] font-bold tracking-[0.18em] uppercase text-xs px-8 py-5 border border-white w-full md:w-auto"
              data-testid="budova-cta-kontakt"
            >
              <span>{t("nav.cta")}</span>
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
